import api from '../httpClient';
import { Appointment } from '@/types';

/** Unwrap `{ data: T }` envelopes used by some backends. */
const unwrapEnvelope = (payload: unknown): unknown => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: unknown }).data;
  }
  return payload;
};

/**
 * Normalizes GET /appointments (and similar) response shapes:
 * `{ appointments, count }`, `{ items, total }`, or nested `{ data: { … } }`.
 */
export const extractAppointmentsArrayFromResponse = (payload: unknown): { list: unknown[]; count?: number } => {
  const root = unwrapEnvelope(payload);
  if (!root || typeof root !== 'object') return { list: [] };
  const r = root as Record<string, unknown>;
  let list: unknown[] = [];
  if (Array.isArray(r.appointments)) list = r.appointments;
  else if (Array.isArray(r.items)) list = r.items;
  else if (Array.isArray(r.data)) list = r.data as unknown[];
  const count =
    typeof r.count === 'number'
      ? r.count
      : typeof r.total === 'number'
        ? r.total
        : list.length;
  return { list, count };
};

// Appointment payload types
export type RecurrencePattern =
  | {
    type: 'daily';
    interval: number;
    endDate?: string;
    occurrenceCount?: number;
  }
  | {
    type: 'weekly';
    interval: number;
    daysOfWeek: number[];
    endDate?: string;
    occurrenceCount?: number;
  }
  | {
    type: 'monthly';
    interval: number;
    daysOfMonth: number[];
    endDate?: string;
    occurrenceCount?: number;
  };

export interface CreateAppointmentPayload {
  clientId: number | string;
  title: string;
  description?: string;
  /** UTC ISO 8601 instant, e.g. `2026-05-20T03:30:00.000Z`. Omit `date` so the backend stores these as UTC. */
  startTime: string;
  /** UTC ISO 8601 instant, e.g. `2026-05-20T04:00:00.000Z`. */
  endTime: string;
  location?: string | null;
  additionalAttendees?: string[];
  appointmentType?: 'online' | 'offline';
  recurrencePattern?: RecurrencePattern;
  /** Internal notes (optional); supported by POST /appointments */
  notes?: string | null;
  meetingLink?: string | null;
}

export type AppointmentAvailableSlot = {
  startTime: string;
  endTime: string;
  startTimeUtc: string;
  endTimeUtc: string;
};

export type AppointmentAvailableSlotsResponse = {
  /** YYYY-MM-DD in the nutritionist's timezone */
  date: string;
  /** Nutritionist IANA timezone — use for interpreting `date` */
  scheduleTimezone: string;
  /** Client display timezone used for slot `startTime` / `endTime` */
  timezone: string;
  duration: number;
  availableSlots: AppointmentAvailableSlot[];
};

const inflightAvailableSlotRequests = new Map<string, Promise<AppointmentAvailableSlotsResponse>>();

/** GET /appointments/available-slots — bookable times for a date and session length. */
export const getAppointmentAvailableSlots = async (params: {
  date: string;
  duration: number;
  /** Client display IANA zone. Omit for nutritionist flows (defaults to nutritionist TZ). */
  timezone?: string;
  /** Required when a client books with a specific nutritionist. */
  nutritionistId?: string;
  appointmentId?: string;
}): Promise<AppointmentAvailableSlotsResponse> => {
  const key = [
    params.date,
    params.duration,
    params.timezone ?? '',
    params.nutritionistId ?? '',
    params.appointmentId ?? '',
  ].join('|');
  const inflight = inflightAvailableSlotRequests.get(key);
  if (inflight) return inflight;

  const request = (async (): Promise<AppointmentAvailableSlotsResponse> => {
    const response = await api.get('/appointments/available-slots', {
      params: {
        date: params.date,
        duration: params.duration,
        ...(params.timezone ? { timezone: params.timezone } : {}),
        ...(params.nutritionistId ? { nutritionistId: params.nutritionistId } : {}),
        ...(params.appointmentId ? { appointmentId: params.appointmentId } : {}),
      },
    });

    const root = unwrapEnvelope(response.data);
    const body =
      root && typeof root === 'object' ? (root as Record<string, unknown>) : {};
    const slots = Array.isArray(body.availableSlots) ? body.availableSlots : [];

    const scheduleTimezone = String(
      body.scheduleTimezone ?? body.timezone ?? params.timezone ?? 'UTC',
    );
    const displayTimezone = String(body.timezone ?? scheduleTimezone);

    return {
      date: String(body.date ?? params.date),
      scheduleTimezone,
      timezone: displayTimezone,
      duration: Number(body.duration ?? params.duration),
      availableSlots: slots
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const slot = item as Record<string, unknown>;
          const startTime = String(slot.startTime ?? '').trim();
          const endTime = String(slot.endTime ?? '').trim();
          const startTimeUtc = String(slot.startTimeUtc ?? '').trim();
          const endTimeUtc = String(slot.endTimeUtc ?? '').trim();
          if (!startTime || !endTime) return null;
          return {
            startTime,
            endTime,
            startTimeUtc,
            endTimeUtc,
          } satisfies AppointmentAvailableSlot;
        })
        .filter((slot): slot is AppointmentAvailableSlot => Boolean(slot)),
    };
  })();

  inflightAvailableSlotRequests.set(key, request);

  try {
    return await request;
  } finally {
    inflightAvailableSlotRequests.delete(key);
  }
};

export interface UpdateAppointmentPayload extends Partial<CreateAppointmentPayload> {
  id: number | string;
}

export interface AppointmentsResponse {
  appointments: Appointment[];
  count: number;
  pagination?: AppointmentsListPagination;
}

export type AppointmentsApiResponse = AppointmentsResponse;

/**
 * JSON fields commonly returned by list appointment endpoints, including
 * `GET /appointments/dashboard` and `GET /appointments`.
 * Supports `{ appointments, count }`, `{ items, total }`, or a `{ data: … }` envelope.
 */
export interface AppointmentsListJsonBody {
  appointments?: unknown[];
  items?: unknown[];
  data?: unknown[];
  count?: number;
  total?: number;
}

export interface AppointmentsDashboardResponse {
  tab: AppointmentsDashboardTab;
  timezone: string;
  filters: {
    appointmentType: string;
    from: string | null;
    to: string | null;
  };
  stats?: {
    today: { totalAppointments: number };
    thisWeek: { inPersonSessions: number; onlineSessions: number; scheduledTotal: number };
  };
  tabCounts: {
    all: number;
    upcoming: number;
    past: number;
  };
  appointments: unknown[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export type AppointmentsListAxiosData = AppointmentsListJsonBody | AppointmentsDashboardResponse | { data: AppointmentsListJsonBody };

export type AppointmentsDashboardTab = 'all' | 'upcoming' | 'past';

/** Query value for GET /appointments/dashboard — matches backend `appointmentType`. */
export type AppointmentsDashboardAppointmentType = 'all' | 'online' | 'offline';

/**
 * Query parameters for `GET /appointments/dashboard`.
 * Example: `?tab=all&timezone=Asia%2FCalcutta&appointmentType=all&from=2026-05-01&to=2026-05-31`
 */
export interface AppointmentsListPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface GetAppointmentsDashboardParams {
  tab: AppointmentsDashboardTab;
  /** IANA zone, e.g. `Asia/Calcutta` */
  timezone: string;
  appointmentType?: AppointmentsDashboardAppointmentType;
  /** Inclusive range start (YYYY-MM-DD). */
  from?: string;
  /** Inclusive range end (YYYY-MM-DD). */
  to?: string;
  page?: number;
  limit?: number;
}

export interface AppointmentsDashboardStats {
  today: { totalAppointments: number };
  thisWeek: {
    inPersonSessions: number;
    onlineSessions: number;
    scheduledTotal: number;
  };
}

export interface AppointmentsDashboardResult {
  appointments: Appointment[];
  count: number;
  stats?: AppointmentsDashboardStats;
  tabCounts?: {
    all: number;
    upcoming: number;
    past: number;
  };
  pagination?: AppointmentsListPagination;
}

/** GET /appointments/dashboard — tab + timezone + optional `appointmentType`, `from` / `to` (YYYY-MM-DD). */
const normalizeListPagination = (
  raw: unknown,
  fallback: { page: number; limit: number; total: number },
): AppointmentsListPagination => {
  if (!raw || typeof raw !== 'object') {
    const totalPages = Math.max(1, Math.ceil(fallback.total / Math.max(1, fallback.limit)));
    return { ...fallback, totalPages };
  }
  const p = raw as Record<string, unknown>;
  const page = Number(p.page);
  const limit = Number(p.limit);
  const total = Number(p.total);
  const totalPages = Number(p.totalPages);
  const resolvedPage = Number.isFinite(page) && page > 0 ? page : fallback.page;
  const resolvedLimit = Number.isFinite(limit) && limit > 0 ? limit : fallback.limit;
  const resolvedTotal = Number.isFinite(total) && total >= 0 ? total : fallback.total;
  const resolvedTotalPages =
    Number.isFinite(totalPages) && totalPages > 0
      ? totalPages
      : Math.max(1, Math.ceil(resolvedTotal / resolvedLimit));
  return {
    page: resolvedPage,
    limit: resolvedLimit,
    total: resolvedTotal,
    totalPages: resolvedTotalPages,
  };
};

export const getAppointmentsDashboard = async (
  params: GetAppointmentsDashboardParams,
): Promise<AppointmentsDashboardResult> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const searchParams = new URLSearchParams();
  searchParams.set('tab', params.tab);
  searchParams.set('timezone', params.timezone);
  searchParams.set('appointmentType', params.appointmentType ?? 'all');
  searchParams.set('page', String(page));
  searchParams.set('limit', String(limit));
  if (params.from) searchParams.set('from', params.from);
  if (params.to) searchParams.set('to', params.to);
  const response = await api.get<AppointmentsListAxiosData>(
    `/appointments/dashboard?${searchParams.toString()}`,
  );

  const data = response.data as AppointmentsDashboardResponse;
  if (data && data.tabCounts) {
    const total = data.pagination?.total ?? data.appointments.length;
    return {
      appointments: data.appointments as Appointment[],
      count: total,
      stats: data.stats,
      tabCounts: data.tabCounts,
      pagination: normalizeListPagination(data.pagination, { page, limit, total }),
    };
  }

  const { list, count } = extractAppointmentsArrayFromResponse(response.data);
  const total = typeof count === 'number' ? count : list.length;
  return {
    appointments: list as Appointment[],
    count: total,
    pagination: normalizeListPagination(
      (response.data as AppointmentsDashboardResponse)?.pagination,
      { page, limit, total },
    ),
  };
};

export const getAppointments = async (params?: {
  limit?: number;
  page?: number;
  clientId?: number | string;
  startDate?: string;
  endDate?: string;
  status?: string; // Filter by status: 'active', 'cancelled', 'completed', etc.
  filter?: 'upcoming' | 'past';
}): Promise<AppointmentsResponse> => {
  const { limit = 100, page = 1, clientId, startDate, endDate, status, filter } = params || {};
  const searchParams = new URLSearchParams();
  searchParams.set('limit', String(limit));
  searchParams.set('page', String(page));
  if (clientId !== undefined && clientId !== null && `${clientId}`.length > 0) {
    searchParams.set('clientId', String(clientId));
  }
  if (startDate) searchParams.set('startDate', startDate);
  if (endDate) searchParams.set('endDate', endDate);
  if (status) searchParams.set('status', status);
  if (filter) searchParams.set('filter', filter);

  const qs = searchParams.toString();
  const response = await api.get<AppointmentsListAxiosData>(`/appointments?${qs}`);
  const { list, count } = extractAppointmentsArrayFromResponse(response.data);
  const total = typeof count === 'number' ? count : list.length;
  const root = unwrapEnvelope(response.data);
  const paginationRaw =
    root && typeof root === 'object' ? (root as Record<string, unknown>).pagination : undefined;
  return {
    appointments: list as Appointment[],
    count: total,
    pagination: normalizeListPagination(paginationRaw, { page, limit, total }),
  };
};

/**
 * Normalizes raw appointment rows from GET /appointments or /appointments/dashboard
 * into the shape used by the appointments UI (calendar + list). Omits cancelled only.
 */
export const mapAppointmentRowsFromApi = (appointmentsArray: unknown[]): Appointment[] => {
  const parseDate = (dateStr: string | Date): Date => {
    if (dateStr instanceof Date) return dateStr;
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  };

  return (Array.isArray(appointmentsArray) ? appointmentsArray : [])
    .map((raw: unknown) => {
      const apt = raw as Record<string, unknown>;
      const startDate = parseDate(apt.startTime as string | Date);
      let endDate: Date;
      if (apt.endTime) {
        endDate = parseDate(apt.endTime as string | Date);
      } else {
        const dm = Number(apt.duration ?? apt.durationMinutes);
        const mins = Number.isFinite(dm) && dm > 0 ? dm : 60;
        endDate = new Date(startDate.getTime() + mins * 60_000);
      }

      const startTimeISO =
        typeof apt.startTime === 'string'
          ? apt.startTime
          : (apt.startTime as Date)?.toISOString?.() || startDate.toISOString();
      const endTimeISO =
        typeof apt.endTime === 'string'
          ? apt.endTime
          : (apt.endTime as Date)?.toISOString?.() || endDate.toISOString();

      const duration = Math.round((endDate.getTime() - startDate.getTime()) / 60000);
      const mode = apt.location === 'In Person' ? 'offline' : 'online';

      return {
        id: apt.id,
        clientId: apt.clientId,
        type: apt.title,
        date: startDate,
        duration,
        mode,
        timezone: apt.timezone,
        isRecurring: apt.isRecurring,
        recurrencePattern: apt.recurrencePattern,
        notes: apt.description,
        status: apt.status,
        title: apt.title,
        description: apt.description,
        meetingLink: apt.meetingLink,
        meeting_link: apt.meetingLink,
        parent_appointment_id: apt.parentAppointmentId,
        location: apt.location,
        appointmentType: apt.appointmentType,
        startTime: startDate,
        endTime: endDate,
        startTimeISO,
        endTimeISO,
        recurringDetails: apt.recurringDetails,
        ...apt,
      } as Appointment;
    })
    .filter((apt: Appointment & { status?: string }) => {
      const status = String(apt.status ?? '').toLowerCase();
      return status !== 'cancelled';
    });
};

// Get appointment by ID
export const getAppointmentById = async (appointmentId: string | number): Promise<{ appointment: Appointment }> => {
  const response = await api.get(`/appointments/${appointmentId}`);
  return response.data;
};

// Create a new appointment
export const createAppointment = async (appointmentData: CreateAppointmentPayload): Promise<{ appointment: Appointment }> => {
  const response = await api.post('/appointments', appointmentData);
  return response.data;
};

// Update an existing appointment
export const updateAppointment = async (
  appointmentId: string | number,
  appointmentData: {
    clientId?: string | number;
    title?: string;
    /** UTC ISO 8601 instant. Omit `date` so the backend uses UTC instants only. */
    startTime?: string;
    /** UTC ISO 8601 instant. */
    endTime?: string;
    description?: string;
    appointmentType?: 'online' | 'offline';
    location?: string;
    additionalAttendees?: string[];
    recurrencePattern?: RecurrencePattern;
    updateScope?: 'this_only' | 'this_and_following' | 'all';
    notes?: string;
    meetingLink?: string;
  }
): Promise<{ appointment: Appointment }> => {
  const response = await api.put(`/appointments/${appointmentId}`, appointmentData);
  return response.data;
};

// Delete an appointment
export const deleteAppointment = async (appointmentId: string | number): Promise<void> => {
  await api.delete(`/appointments/${appointmentId}`);
};

// Cancel an appointment
export const cancelAppointment = async (
  appointmentId: string | number,
  cancelScope?: 'this_only' | 'this_and_following' | 'all'
): Promise<{ appointment: Appointment }> => {
  const payload: { cancelScope?: string } = {};
  if (cancelScope) {
    payload.cancelScope = cancelScope;
  }
  const response = await api.post(`/appointments/${appointmentId}/cancel`, payload);
  return response.data;
};

export interface CreateBookingLinkPayload {
  clientId?: string | null;
  expiresInDays?: number;
}

export interface CreateBookingLinkResponse {
  message: string;
  data: {
    linkId: string;
    expiresAt: string;
  };
}

export const createBookingLink = async (payload: CreateBookingLinkPayload): Promise<CreateBookingLinkResponse> => {
  const response = await api.post('/appointments/booking-links', payload);
  return response.data;
};

export interface CreateRescheduleLinkPayload {
  appointmentId: string;
  expiresInDays?: number;
}

export interface CreateRescheduleLinkResponse {
  message?: string;
  data: {
    linkId: string;
    expiresAt: string;
  };
}

export const createRescheduleLink = async (payload: CreateRescheduleLinkPayload): Promise<CreateRescheduleLinkResponse> => {
  const response = await api.post('/appointments/reschedule-links', payload);
  return response.data;
};

/** Single nutritionist-authored note on an appointment (embedded on GET /appointments/:id; added via POST /appointments/:id/notes). */
export type AppointmentSessionNote = {
  id: string;
  text: string;
  createdAt?: string;
  authorName?: string;
};

function normalizeAppointmentSessionNote(item: unknown, index: number): AppointmentSessionNote | null {
  if (!item || typeof item !== 'object') return null;
  const o = item as Record<string, unknown>;
  const text = String(o.text ?? o.body ?? o.content ?? '').trim();
  if (!text) return null;
  const id = String(o.id ?? o._id ?? `note-${index}`);
  const createdAt = o.createdAt ?? o.created_at ?? o.updatedAt ?? o.updated_at;
  const authorName = o.authorName ?? o.author_name ?? o.createdBy ?? o.created_by ?? o.nutritionistName ?? o.userName;
  return {
    id,
    text,
    createdAt: createdAt != null ? String(createdAt) : undefined,
    authorName: authorName != null ? String(authorName) : undefined,
  };
}

/**
 * Reads nutritionist session notes from a single appointment payload (GET /appointments/:id).
 * Notes are not fetched from GET /appointments/:id/notes — only POST adds notes; the list is on the appointment.
 */
export function getSessionNotesFromAppointment(appointment: unknown): AppointmentSessionNote[] {
  if (!appointment || typeof appointment !== 'object') return [];
  const root = unwrapEnvelope(appointment);
  const a = (root && typeof root === 'object' ? root : appointment) as Record<string, unknown>;

  const listKeys = [
    'nutritionistNotes',
    'nutritionist_notes',
    'appointmentNotes',
    'appointment_notes',
    'sessionNotes',
    'session_notes',
    'staffNotes',
    'staff_notes',
  ] as const;
  for (const key of listKeys) {
    const arr = a[key];
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.map(normalizeAppointmentSessionNote).filter((n): n is AppointmentSessionNote => Boolean(n));
    }
  }

  const notesArr = a.notes;
  if (Array.isArray(notesArr) && notesArr.length > 0 && typeof notesArr[0] === 'object') {
    return notesArr.map(normalizeAppointmentSessionNote).filter((n): n is AppointmentSessionNote => Boolean(n));
  }

  return [];
}

/** POST `/appointments/{appointmentId}/notes` — body `{ text: string }`; multiple notes allowed. */
export const postAppointmentNote = async (
  appointmentId: string | number,
  payload: { text: string },
): Promise<void> => {
  await api.post(`/appointments/${appointmentId}/notes`, payload);
};

// Check appointment availability
export interface CheckAvailabilityPayload {
  date: string; // YYYY-MM-DD format
  startTime: string; // 12-hour format with AM/PM (e.g., "12:00 PM")
  endTime: string; // 12-hour format with AM/PM (e.g., "1:00 PM")
  timezone: string;
  clientId?: string | number; // Optional: include when checking for updates
  appointmentId?: string | number; // Optional: exclude current appointment when updating
  recurrencePattern?: RecurrencePattern; // Optional: include for recurring appointments
}

export interface ConflictInfo {
  startTime: string;
  endTime: string;
  clientName: string;
  clientEmail?: string;
  clientId?: string;
  appointmentId?: string | number;
  recurringSeriesEndDate?: string | null;
}

export interface CheckAvailabilityResponse {
  hasConflict: boolean;
  conflicts?: ConflictInfo[];
  message?: string;
  // For backward compatibility, also support 'available' field
  available?: boolean;
}

export const checkAvailability = async (payload: CheckAvailabilityPayload): Promise<CheckAvailabilityResponse> => {
  const response = await api.post('/appointments/check-availability', payload);
  return response.data;
};
